'use client';

import { createContext, useEffect, useState, useContext } from 'react';
import type { ReactNode } from 'react';
import { createHighlighter } from 'shiki';
import type { HighlighterGeneric, BundledLanguage, BundledTheme } from 'shiki';

import { highlighterThemes } from './highlighterThemes';

type Highlighter = HighlighterGeneric<BundledLanguage, BundledTheme>;

interface HighlighterContext {
    highlighter?: Highlighter;
}

const { light, dark } = highlighterThemes;

const Context = createContext<HighlighterContext>({});

/**
 * @returns a context provider whose value includes a `highlighter` instance from `shiki` that
 * can be used to perform syntax highlighting.
 */
export const HighlighterProvider = ({ children }: { children: ReactNode }) => {
    const [highlighter, setHighlighter] = useState<Highlighter | undefined>(
        undefined,
    );

    useEffect(() => {
        if (highlighter === undefined) {
            createHighlighter({
                langs: ['python', 'text'],
                // @ts-expect-error -- there's a type error here but these themes *should* match the
                // example here: https://raw.githubusercontent.com/shikijs/textmate-grammars-themes/refs/heads/main/packages/tm-themes/themes/dark-plus.json
                themes: [light, dark],
            })
                .then((value) => {
                    setHighlighter(value);
                })
                .catch((e) => {
                    console.error('Error when creating shiki highlighter:', e);
                });
        }
    }, [highlighter]);

    return (
        <Context.Provider value={{ highlighter }}>{children}</Context.Provider>
    );
};

type UseHighlighter = () => HighlighterContext;

export const useHighlighter: UseHighlighter = () => useContext(Context);
